import { isActionOf, getType } from 'typesafe-actions'
import { from, of } from 'rxjs'
import { catchError, exhaustMap, filter, map, mergeMap, switchMap } from 'rxjs/operators'
import { toast } from 'react-toastify'
import { AxiosError } from 'axios'
import { RootEpic } from '../types'
import {
  getSfWalletsFailure,
  getSfWalletsRequest,
  getSfWalletsSuccess,
  createSfWalletRequest,
  createSfWalletSuccess,
  createSfWalletFailure,
  updateSfWalletRequest,
  updateSfWalletSuccess,
  updateSfWalletFailure,
  getAssignedWalletsByUserRequest,
  getAssignedWalletsByUserSuccess,
  getAssignedWalletsByUserFailure,
  assignSfWalletToUserRequest,
  assignSfWalletToUserSuccess,
  assignSfWalletToUserFailure,
  unassignSfWalletFromUserRequest,
  unassignSfWalletFromUserSuccess,
  unassignSfWalletFromUserFailure,
  getAssignedWalletIdsByUserRequest,
  getAssignedWalletIdsByUserSuccess,
  getAssignedWalletIdsByUserFailure,
} from '../actions'

export const fetchSfWalletsEpic: RootEpic = (action$, state$, { fetchSfWallets }) => {
  return action$.pipe(
    filter(isActionOf([getSfWalletsRequest, createSfWalletSuccess, updateSfWalletSuccess])),
    switchMap(({ type, payload }) =>
      from(
        fetchSfWallets({
          token: state$.value.app.authToken as string,
          limit: 10,
          ...state$.value.sfWallets.filters,
          ...(type === getType(getSfWalletsRequest) && payload),
        }),
      ).pipe(
        map((response) => getSfWalletsSuccess(response)),
        catchError((message) => {
          toast.error('Failed to fetch wallets.')
          return of(getSfWalletsFailure(message))
        }),
      ),
    ),
  )
}

export const createSfWalletsEpic: RootEpic = (action$, state$, { createSfWallets }) => {
  return action$.pipe(
    filter(isActionOf([createSfWalletRequest])),
    exhaustMap(({ payload }) =>
      from(
        createSfWallets({
          token: state$.value.app.authToken as string,
          ...payload,
        }),
      ).pipe(
        map((response) => {
          toast.success('Wallet successfully created.')
          return createSfWalletSuccess(response)
        }),
        catchError((error: AxiosError) => {
          toast.error(error.response?.data.message || 'Failed to create wallet.')
          return of(createSfWalletFailure(error))
        }),
      ),
    ),
  )
}

export const updateSfWalletsEpic: RootEpic = (action$, state$, { updateSfWallets }) => {
  return action$.pipe(
    filter(isActionOf([updateSfWalletRequest])),
    exhaustMap(({ payload }) =>
      from(
        updateSfWallets({
          token: state$.value.app.authToken as string,
          ...payload,
        }),
      ).pipe(
        map((response) => {
          toast.success('Wallet successfully updated.')
          return updateSfWalletSuccess(response)
        }),
        catchError((error: AxiosError) => {
          toast.error(error.response?.data.message || 'Failed to update wallet.')
          return of(updateSfWalletFailure(error))
        }),
      ),
    ),
  )
}

export const getAssignedWalletsByUserEpic: RootEpic = (action$, state$, { getAssignedWalletsByUser }) => {
  return action$.pipe(
    filter(isActionOf([getAssignedWalletsByUserRequest])),
    switchMap(({ payload: { idSf, page, count } }: ReturnType<typeof getAssignedWalletsByUserRequest>) =>
      from(
        getAssignedWalletsByUser({
          token: state$.value.app.authToken as string,
          idSf,
          ...(page && { page }),
          ...(count && { count }),
        }),
      ).pipe(
        mergeMap((response) =>
          !response.items.length && page && page > 1
            ? of(getAssignedWalletsByUserRequest({ idSf, page: page - 1 }))
            : of(getAssignedWalletsByUserSuccess(response)),
        ),
        catchError((message) => {
          toast.error('Failed: ', message)
          return of(getAssignedWalletsByUserFailure(message))
        }),
      ),
    ),
  )
}

export const getAssignedWalletIdsByUserEpic: RootEpic = (action$, state$, { getAssignedWalletIdsByUser }) => {
  return action$.pipe(
    filter(isActionOf([getAssignedWalletIdsByUserRequest, getAssignedWalletsByUserRequest])),
    exhaustMap(({ payload: { idSf } }) =>
      from(
        getAssignedWalletIdsByUser({
          token: state$.value.app.authToken as string,
          idSf,
        }),
      ).pipe(
        map((response) => getAssignedWalletIdsByUserSuccess(response)),
        catchError((message) => {
          toast.error('Failed to fetch bank account ids.')
          return of(getAssignedWalletIdsByUserFailure(message))
        }),
      ),
    ),
  )
}

export const assignSfWalletsToUserEpic: RootEpic = (action$, state$, { assignSfWalletToUser }) => {
  return action$.pipe(
    filter(isActionOf([assignSfWalletToUserRequest])),
    exhaustMap(({ payload }) =>
      from(
        assignSfWalletToUser({
          token: state$.value.app.authToken as string,
          ...payload,
        }),
      ).pipe(
        mergeMap((response) => {
          if (response.success) {
            toast.success('Wallet has been added successfully.')
            return of(
              assignSfWalletToUserSuccess(response),
              getAssignedWalletsByUserRequest({
                idSf: payload.userIdSf,
                page: state$.value.sfWallets.assignedUserWallets.data.meta.currentPage,
              }),
            )
          }
          return of(assignSfWalletToUserSuccess(response))
        }),
        catchError((message) => {
          toast.error('Failed: ', message)
          return of(assignSfWalletToUserFailure(message))
        }),
      ),
    ),
  )
}

export const unassignSfWalletToUserEpic: RootEpic = (action$, state$, { unassignSfWalletFromUser }) => {
  return action$.pipe(
    filter(isActionOf([unassignSfWalletFromUserRequest])),
    exhaustMap(({ payload }) =>
      from(
        unassignSfWalletFromUser({
          token: state$.value.app.authToken as string,
          ...payload,
        }),
      ).pipe(
        mergeMap((response) => {
          if (response.success) {
            toast.success('Wallet has been unassigned successfully.')
            return of(
              unassignSfWalletFromUserSuccess(response),
              getAssignedWalletsByUserRequest({
                idSf: payload.userIdSf,
                page: state$.value.sfWallets.assignedUserWallets.data.meta.currentPage,
              }),
            )
          }
          return of(unassignSfWalletFromUserSuccess(response))
        }),
        catchError((message) => {
          toast.error('Failed: ', message)
          return of(unassignSfWalletFromUserFailure(message))
        }),
      ),
    ),
  )
}

export default [
  fetchSfWalletsEpic,
  createSfWalletsEpic,
  updateSfWalletsEpic,
  getAssignedWalletsByUserEpic,
  getAssignedWalletIdsByUserEpic,
  assignSfWalletsToUserEpic,
  unassignSfWalletToUserEpic,
]
